// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - DAILY LIMIT INDICATOR
// Instant refresh via custom event — no 60s delay after purge
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
  getDailyRemaining,
  getDailyLimit,
  getDailyUsed,
  getResetCountdown,
  getLastKnownToiletStatus,
} from './dailyLimitsManager';
import './DailyLimitIndicator.css';

export default function DailyLimitIndicator({ isToiletMode = false }) {
  const getEffective = () => isToiletMode || getLastKnownToiletStatus();

  const buildData = useCallback(() => {
    const effective = getEffective();
    return {
      used: getDailyUsed(),
      limit: getDailyLimit(effective),
      remaining: getDailyRemaining(effective),
      resetIn: getResetCountdown(),
    };
  }, [isToiletMode]);

  const [data, setData] = useState(buildData);

  useEffect(() => {
    // Refresh immediately when toilet mode prop changes
    setData(buildData());

    // 🔔 Listen for purge events — updates instantly after each purge
    const handlePurge = () => setData(buildData());
    window.addEventListener('distil:purge', handlePurge);

    // Also refresh every minute for countdown
    const interval = setInterval(() => setData(buildData()), 60_000);

    return () => {
      window.removeEventListener('distil:purge', handlePurge);
      clearInterval(interval);
    };
  }, [isToiletMode, buildData]);

  const { used, limit, remaining, resetIn } = data;
  const percentage = Math.min(100, (used / limit) * 100);
  const isExhausted = remaining === 0;
  const isLow = remaining <= 5 && remaining > 0;
  const showToilet = getEffective();

  return (
    <div className={`daily-limit-indicator ${showToilet ? 'toilet-mode' : ''} ${isExhausted ? 'exhausted' : ''} ${isLow ? 'low' : ''}`}>
      <div className="dli-header">
        <div className="dli-title-row">
          <span className="dli-icon">{showToilet ? '🚽' : '🔥'}</span>
          <span className="dli-title">Daily Cleanups</span>
        </div>
        <div className="dli-count">
          <span className={`dli-remaining ${isExhausted ? 'zero' : ''}`}>{remaining}</span>
          <span className="dli-sep">/</span>
          <span className="dli-limit">{limit}</span>
          <span className="dli-label">left</span>
        </div>
      </div>

      <div className="dli-bar-track">
        <div className="dli-bar-fill" style={{ width: `${percentage}%` }} />
      </div>

      <div className="dli-footer">
        <span className="dli-reset">Resets in {resetIn}</span>
        {!showToilet && (
          <span className="dli-upgrade">💎 Toilet Mode: 50/day</span>
        )}
        {isExhausted && (
          <span className="dli-exhausted-msg">Come back tomorrow!</span>
        )}
      </div>
    </div>
  );
}
